import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import * as moment from "moment";
import { DateSollecitoDocumentazione } from "src/app/models/date-sollecito-documentazione";
import { DateSollecitoDocumentazioneService } from "src/app/services/date-sollecito-documentazione.service";

export interface DateSollecitoDocumentazioneDialogData {
  incaricoId: number;
  incaricoLabel?: string;
  onUpdate?: (count: number, earliest: moment.Moment | null) => void;
}

@Component({
  selector: "app-date-sollecito-documentazione-history-dialog",
  templateUrl: "./date-sollecito-documentazione-history-dialog.component.html",
  styleUrls: ["./date-sollecito-documentazione-history-dialog.component.scss"],
})
export class DateSollecitoDocumentazioneHistoryDialogComponent implements OnInit {
  entries: DateSollecitoDocumentazione[] = [];
  loading = false;
  error: string | null = null;
  private isClosing = false;
  private hasMutated = false;

  constructor(
    private dialogRef: MatDialogRef<DateSollecitoDocumentazioneHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DateSollecitoDocumentazioneDialogData,
    private dateSollecitoDocumentazioneService: DateSollecitoDocumentazioneService,
    private toastr: ToastrService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadEntries();

    this.dialogRef
      .backdropClick()
      .subscribe(() => this.closeWithResult(false));
    this.dialogRef
      .keydownEvents()
      .subscribe((event: KeyboardEvent) => {
        if (event.key === "Escape") {
          this.closeWithResult(false);
        }
      });
  }

  trackByEntryId(_index: number, item: DateSollecitoDocumentazione): number {
    return item.id;
  }

  async loadEntries(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      this.entries = await this.dateSollecitoDocumentazioneService.listByIncarico(
        this.data.incaricoId
      );
    } catch (error) {
      console.error("Errore caricamento date sollecito documentazione:", error);
      this.error = "Errore durante il caricamento delle date sollecito documentazione.";
    } finally {
      this.loading = false;
    }
  }

  async remove(entry: DateSollecitoDocumentazione): Promise<void> {
    if (
      !window.confirm(
        "Sei sicuro di voler eliminare questa data di sollecito documentazione?"
      )
    ) {
      return;
    }

    try {
      await this.dateSollecitoDocumentazioneService.delete(entry);
      this.entries = this.entries.filter((item) => item.id !== entry.id);
      this.hasMutated = true;
      this.notifyCount();
      this.toastr.success("Data sollecito documentazione rimossa.");
    } catch (error) {
      console.error("Errore eliminazione data sollecito documentazione:", error);
      this.toastr.error("Impossibile eliminare la data sollecito documentazione.");
    }
  }

  closeWithResult(triggeredByAction = true): void {
    if (this.isClosing) {
      return;
    }
    this.isClosing = true;
    if (!this.hasMutated) {
      this.dialogRef.close();
      return;
    }

    const earliest = this.getEarliestMoment();
    this.dialogRef.close({
      remainingCount: this.entries.length,
      earliestValore: earliest ? earliest.toISOString() : null,
    });
  }

  private notifyCount(): void {
    if (!this.hasMutated) return;
    const earliest = this.getEarliestMoment();
    if (typeof this.data?.onUpdate === "function") {
      this.data.onUpdate(this.entries.length, earliest);
    }
  }

  private getEarliestMoment(): moment.Moment | null {
    const candidates = this.entries
      .map((entry) => entry.valore)
      .filter((value): value is moment.Moment => !!value);

    if (!candidates.length) {
      return null;
    }

    return candidates.reduce((earliest, current) =>
      current.isBefore(earliest) ? current : earliest
    );
  }
}
